import type { SuggestedChip } from "@/lib/api/chat";

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[-_/\\]/g, " ")
    .replace(/['".,!?;:()[\]{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export const CHAT_RESPONSE_STYLE_RULES = [
  "RESPONSE STYLE RULES (follow these strictly):",
  "- Explain things like the user is brand new to SpecTree.",
  "- Start with the answer in one short sentence.",
  "- Use short paragraphs or short bullets only when they help.",
  "- Keep each bullet or step to one sentence when possible.",
  "- No markdown headings, no tables, no walls of text, and no filler.",
  "- Use plain everyday language. Avoid jargon and corporate-speak.",
  "- Only use backticks for real commands, paths, or exact labels to click.",
  "- If you mention a command, say where to paste it before you show it.",
  "- Do not mention docs, guides, or external links. Give the answer directly.",
  "- Do not offer a walkthrough unless the user is asking about a page, screen, or tour.",
].join("\n");

const PAGE_TOUR_PATTERNS = [
  "walk me through this page",
  "walk me through this screen",
  "walk me through this",
  "show me around",
  "give me a tour",
  "tour this page",
  "tour this screen",
  "what can i do here",
  "what can i do on this page",
  "what does this page do",
  "what is on this page",
  "explain this page",
  "explain this screen",
  "how does this page work",
];

function isPageTourChip(chip: SuggestedChip): boolean {
  const normalizedLabel = normalizeText(chip.label);
  const normalizedValue = normalizeText(chip.value);

  return (
    normalizedLabel.includes("walk me through") ||
    normalizedValue.includes("give me a tour of everything on this page") ||
    normalizedValue.includes("walk me through this page")
  );
}

export function buildChatPrompt(
  question: string,
  groundingContext?: string,
): string {
  const parts: string[] = [];

  if (groundingContext) {
    parts.push(
      "[Relevant SpecTree context for this query]",
      groundingContext,
      "[End context]",
      "",
    );
  }

  parts.push(CHAT_RESPONSE_STYLE_RULES, "", `User question: ${question}`);

  return parts.join("\n");
}

export function isExplicitPageTourRequest(text: string): boolean {
  const normalized = normalizeText(text);
  return PAGE_TOUR_PATTERNS.some((pattern) => normalized.includes(pattern));
}

export function filterPageTourChips(
  chips: readonly SuggestedChip[],
  shouldOfferPageTour: boolean,
): SuggestedChip[] {
  if (shouldOfferPageTour) {
    return [...chips];
  }

  return chips.filter((chip) => !isPageTourChip(chip));
}
